/**
 * Tab Switcher 组件
 * 功能：
 * 1. 点击 tab 切换显示/隐藏页面上对应 id 的元素
 * 2. 支持 sticky 悬浮（借鉴 tabs.liquid 的 ResizeObserver 监听 #top 动态计算 top）
 * 3. 点击 tab 后滚动回组件位置（借鉴 category-bar 的滚动逻辑）
 */
class TabSwitcher extends HTMLElement {
  constructor() {
    super();
    this.isSticky = this.dataset.sticky === 'true';
    this.tabs = this.querySelectorAll('.tab-switcher__item');
    this.tabList = this.querySelector('.tab-switcher__list');
    this.currentIndex = 0;
    this.stickyReady = false;
    this.init();
  }

  init() {
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.switchTab(index));
    });

    // 初始显隐状态
    this.applyVisibility(0);

    // sticky 处理：使用 ResizeObserver 监听 #top 高度变化
    if (this.isSticky) {
      this.initSticky();
    }
  }

  initSticky() {
    const sectionEl = document.getElementById('shopify-section-' + this.closest('[id^="shopify-section-"]').id.replace('shopify-section-', ''));
    this.sectionEl = sectionEl || this.closest('[id^="shopify-section-"]');

    const topEl = document.getElementById('top');
    if (topEl) {
      let lastHeight = 0;
      const observer = new ResizeObserver(() => {
        const topHeight = topEl.offsetHeight - 1;
        if (lastHeight !== topHeight) {
          lastHeight = topHeight;
          if (this.sectionEl) {
            this.sectionEl.style.top = topHeight + 'px';
          }
        }
      });
      observer.observe(topEl);
    } else {
      const header = document.querySelector('header-container');
      if (header && this.sectionEl) {
        this.sectionEl.style.top = header.offsetHeight + 'px';
      }
    }

    this.stickyReady = true;
  }

  switchTab(index) {
    if (index === this.currentIndex) return;

    // 切换 active 状态
    this.tabs[this.currentIndex].classList.remove('active');
    this.tabs[index].classList.add('active');
    this.currentIndex = index;

    // 切换显隐
    this.applyVisibility(index);

    // sticky 模式下，点击后滚动回当前组件位置
    if (this.isSticky && this.stickyReady) {
      requestAnimationFrame(() => {
        const headerEl = document.querySelector('header-container');
        const headerHeight = headerEl ? headerEl.offsetHeight : 0;
        const selfHeight = this.sectionEl ? this.sectionEl.offsetHeight : this.offsetHeight;

        // 获取当前激活 tab 的第一个目标元素来定位
        const activeTab = this.tabs[index];
        const ids = this.getTargetIds(activeTab);
        if (ids.length > 0) {
          const firstTarget = document.getElementById(ids[0]);
          if (firstTarget) {
            // 滚动到目标元素顶部刚好在 tab 栏下方
            const targetTop = firstTarget.getBoundingClientRect().top + window.scrollY - headerHeight - selfHeight;
            window.scrollTo({
              top: targetTop,
              behavior: 'smooth',
            });
            return;
          }
        }

        // fallback: 滚动到 tab 组件上方
        if (this.sectionEl) {
          const rect = this.sectionEl.getBoundingClientRect();
          if (rect.top < 0) {
            window.scrollTo({
              top: window.scrollY + rect.top - headerHeight,
              behavior: 'smooth',
            });
          }
        }
      });
    }
  }

  applyVisibility(activeIndex) {
    this.tabs.forEach((tab, index) => {
      const ids = this.getTargetIds(tab);
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        if (index === activeIndex) {
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      });
    });
  }

  getTargetIds(tab) {
    const raw = tab.dataset.targetIds || '';
    if (!raw.trim()) return [];
    return raw.split('+').map((s) => s.trim()).filter(Boolean);
  }
}

document.addEventListener('DOMContentLoaded', function () {
  if (!customElements.get('tab-switcher')) {
    customElements.define('tab-switcher', TabSwitcher);
  }
});
